/**
 * useOnlineStatus — is the device connected?
 *
 * Used by: OfflineBanner and SyncStatusPill in all three shells, and by list
 * screens to show "dữ liệu có thể đã cũ" instead of an error when a refetch
 * fails while offline.
 *
 * `navigator.onLine` only proves the device has a network interface, not that
 * the API is reachable (captive portals, a dead uplink, a Worker outage all
 * report "online"). So the hook ALSO accepts an optional heartbeat: the app
 * passes the timestamp of its last successful API response, and if that goes
 * stale we report offline even though the OS disagrees. POS relies on this —
 * being wrong about connectivity means orders silently not reaching the kitchen.
 */
import { useEffect, useState } from 'react';

export interface OnlineStatusOptions {
  /** Epoch ms of the last successful API response. */
  readonly lastSuccessAt?: number | null;
  /** Consider the connection dead after this long without a success. */
  readonly staleAfterMs?: number;
}

export function useOnlineStatus(options: OnlineStatusOptions = {}): boolean {
  const { lastSuccessAt = null, staleAfterMs } = options;

  const [browserOnline, setBrowserOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const goOnline = (): void => setBrowserOnline(true);
    const goOffline = (): void => setBrowserOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Re-render periodically so the staleness check actually expires.
  const [, tick] = useState(0);
  useEffect(() => {
    if (staleAfterMs === undefined) return undefined;
    const timer = window.setInterval(() => tick((n) => n + 1), Math.min(staleAfterMs, 15_000));
    return () => window.clearInterval(timer);
  }, [staleAfterMs]);

  if (!browserOnline) return false;
  if (staleAfterMs === undefined || lastSuccessAt === null) return true;
  return Date.now() - lastSuccessAt < staleAfterMs;
}
